import { useState, type FormEvent } from "react";
import SectionHeading from "./SectionHeading";

const SUBMIT_URL = "https://readdy.ai/api/form/dafupvrep053ijub203g";

type Status = { type: "idle" | "loading" | "success" | "error"; msg: string };

export default function ContactSection() {
  const [status, setStatus] = useState<Status>({ type: "idle", msg: "" });

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = ((fd.get("contact_alt") as string) || "").trim();
    if (honeypot) {
      setStatus({ type: "success", msg: "已收到，感谢你的关注。" });
      form.reset();
      return;
    }
    fd.delete("contact_alt");

    const name = ((fd.get("name") as string) || "").trim();
    const email = ((fd.get("email") as string) || "").trim();
    const msg = ((fd.get("message") as string) || "").trim();
    if (!name || !email || !msg) {
      setStatus({ type: "error", msg: "请完整填写必填项后提交。" });
      return;
    }

    setStatus({ type: "loading", msg: "正在提交……" });
    try {
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Iterable<[string, string]>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta as Record<string, unknown>) || {};
      const serverMsg =
        (meta?.message as string) ||
        (parsed?.message as string) ||
        (meta?.detail as string) ||
        "";
      const code = (parsed?.code as string) || "";

      if (res.ok && code === "OK") {
        setStatus({ type: "success", msg: serverMsg || "提交成功，我们会尽快核查。" });
        form.reset();
      } else {
        setStatus({
          type: "error",
          msg:
            serverMsg && serverMsg.toLowerCase().includes("spam")
              ? "检测到疑似异常提交，请稍后再试。"
              : serverMsg || "提交失败，请稍后重试。",
        });
      }
    } catch {
      setStatus({ type: "error", msg: "网络异常，提交失败，请稍后重试。" });
    }
  }

  return (
    <section id="contact" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <SectionHeading
              align="left"
              eyebrow="提交线索 · 联系我们"
              title="刷到可疑黑料？交给我们来查"
              desc="填写你看到的原始链接或截图说明，附上你的疑虑点即可。核查前我们会先做信源分级与初筛，符合条件的话题进入正式核查流程。"
            />

            <ul className="space-y-4 mt-2">
              {[
                {
                  icon: "ri-mail-line",
                  title: "邮箱",
                  value: "contact@qiuzhen-hecha.example",
                  sub: "适合发送长文素材与资料包",
                },
                {
                  icon: "ri-time-line",
                  title: "核查周期",
                  value: "常规 3–5 个工作日",
                  sub: "高关注度话题会优先排队核查",
                },
                {
                  icon: "ri-chat-3-line",
                  title: "反馈渠道",
                  value: "站内线索表单",
                  sub: "每周统一汇总并公开处理进度",
                },
              ].map((c) => (
                <li
                  key={c.title}
                  className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-100/60 p-5"
                >
                  <span className="w-11 h-11 rounded-md bg-primary-500 text-background-50 flex items-center justify-center shrink-0">
                    <i className={`${c.icon} text-lg`}></i>
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-bold text-foreground-950">
                      {c.title}
                    </h3>
                    <p className="mt-0.5 text-sm font-medium text-primary-700">{c.value}</p>
                    <p className="text-xs text-foreground-600 mt-0.5">{c.sub}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100/60 p-6 md:p-8">
            <h3 className="font-heading text-lg font-bold text-foreground-950">
              提交待核实线索
            </h3>
            <p className="mt-1 text-xs text-foreground-600">
              带 <span className="text-accent-700">*</span> 为必填项，线索建议尽量附上原链接。
            </p>

            <form
              data-readdy-form="online-blackliao-tip-form"
              onSubmit={handleSubmit}
              className="mt-5 space-y-4"
              noValidate
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="tip-name" className="block text-sm font-medium text-foreground-800">
                    你的称呼 <span className="text-accent-700">*</span>
                  </label>
                  <input
                    id="tip-name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你"
                    className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="tip-email" className="block text-sm font-medium text-foreground-800">
                    联系邮箱 <span className="text-accent-700">*</span>
                  </label>
                  <input
                    id="tip-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="tip-topic" className="block text-sm font-medium text-foreground-800">
                  线索类型
                </label>
                <select
                  id="tip-topic"
                  name="topic"
                  className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                >
                  <option value="健康养生">健康养生传闻</option>
                  <option value="财经消费">财经消费传闻</option>
                  <option value="数码科技">数码科技传闻</option>
                  <option value="社会民生">社会民生传闻</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div>
                <label htmlFor="tip-link" className="block text-sm font-medium text-foreground-800">
                  原始链接或出处
                </label>
                <input
                  id="tip-link"
                  name="source_url"
                  type="url"
                  placeholder="粘贴疑似不实信息的原链接"
                  className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>

              <div>
                <label htmlFor="tip-message" className="block text-sm font-medium text-foreground-800">
                  你的疑虑说明 <span className="text-accent-700">*</span>
                </label>
                <textarea
                  id="tip-message"
                  name="message"
                  required
                  rows={4}
                  maxLength={500}
                  placeholder="简要说明你觉得可疑的点，或附上你了解到的信息"
                  className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                ></textarea>
              </div>

              <input
                type="text"
                name="contact_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="verification-scan"
              />

              <button
                type="submit"
                disabled={status.type === "loading"}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 px-5 py-3 text-sm font-semibold whitespace-nowrap transition-colors"
              >
                <i className="ri-send-plane-line"></i>
                {status.type === "loading" ? "提交中……" : "提交线索"}
              </button>

              {status.type === "success" && (
                <p className="rounded-md bg-primary-100 border border-primary-200 text-primary-800 px-4 py-3 text-sm">
                  <i className="ri-checkbox-circle-line mr-1"></i>
                  {status.msg}
                </p>
              )}
              {status.type === "error" && (
                <p className="rounded-md bg-accent-100 border border-accent-300 text-accent-900 px-4 py-3 text-sm">
                  <i className="ri-error-warning-line mr-1"></i>
                  {status.msg}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}